import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cset_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cset_token');
      localStorage.removeItem('cset_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  isLocal: () => api.get('/auth/islocal'),
};

// Assessment API
export const assessmentAPI = {
  getAll: () => api.get('/assessmentsforuser'),
  create: (data) => api.post('/assessment/create', data),
  getDetail: (id) => api.get(`/assessmentdetail/${id}`),
  delete: (id) => api.delete(`/assessment/${id}`),
  getReadiness: (id) => api.get(`/assessment/${id}/readiness`),
};

// Questions API
export const questionsAPI = {
  getQuestions: (assessmentId) => api.get(`/QuestionList/${assessmentId}`),
  submitAnswer: (assessmentId, data) => api.post(`/AnswerQuestion/${assessmentId}`, data),
};

// Reports API
export const reportsAPI = {
  getInfo: (assessmentId) => api.get(`/reports/info/${assessmentId}`),
  getScores: (assessmentId) => api.get(`/reports/scores/${assessmentId}`),
  getCmmcScores: (assessmentId) => api.get(`/cmmc/scores/${assessmentId}`),
  getHipaaScores: (assessmentId) => api.get(`/hipaa/scores/${assessmentId}`),
  getDeficiencies: (assessmentId) => api.get(`/reports/deficiency/${assessmentId}`),
  getExecutiveSummary: (assessmentId) => api.get(`/assessment/${assessmentId}/executive-summary`),
  getCMMCDomainScores: (assessmentId) => api.get(`/cmmc/scores/${assessmentId}`),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getAllAssessments: () => api.get('/admin/assessments'),
  getStats: () => api.get('/admin/stats'),
  // User management
  createUser: (data) => api.post('/admin/users', data),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  resetUserPassword: (userId, newPassword) => api.put(`/admin/users/${userId}/password`, { newPassword }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Client management
  getClients: () => api.get('/admin/clients'),
  createClient: (data) => api.post('/admin/clients', data),
  deleteClient: (clientId) => api.delete(`/admin/clients/${clientId}`),
  assignAssessment: (data) => api.post('/admin/clients/assign', data),
  unassignAssessment: (clientId, assessmentId) => api.delete(`/admin/clients/${clientId}/assessments/${assessmentId}`),
  // Data Room
  getDataRoomFiles: (clientId) => api.get(`/admin/dataroom/${clientId}`),
  uploadDataRoomFile: (clientId, file, description) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description || '');
    return api.post(`/admin/dataroom/${clientId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDataRoomFile: (clientId, fileId) => api.delete(`/admin/dataroom/${clientId}/file/${fileId}`),
  getDataRoomDownloadUrl: (clientId, fileId) => `${BACKEND_URL}/api/admin/dataroom/${clientId}/download/${fileId}`,
};

// Question CMS API (Admin only)
export const questionCmsAPI = {
  getQuestions: (assessmentType) => api.get(`/cms/questions/${assessmentType}`),
  getQuestionDetail: (assessmentType, questionId) => api.get(`/cms/questions/${assessmentType}/${questionId}`),
  updateQuestion: (assessmentType, questionId, data) => api.put(`/cms/questions/${assessmentType}/${questionId}`, data),
  resetQuestion: (assessmentType, questionId) => api.delete(`/cms/questions/${assessmentType}/${questionId}`),
  getOverrides: (assessmentType) => api.get(`/cms/overrides/${assessmentType}`),
  exportQuestions: (assessmentType, format = 'csv') => api.get(`/cms/export/${assessmentType}?format=${format}`, { responseType: 'blob' }),
  previewImport: (assessmentType, data) => api.post(`/cms/import/${assessmentType}/preview`, data),
  applyImport: (assessmentType, data) => api.post(`/cms/import/${assessmentType}/apply`, data),
  // CMS Evidence
  uploadEvidence: (assessmentType, questionId, formData) => api.post(`/cms/evidence/${assessmentType}/${questionId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listEvidence: (assessmentType, questionId) => api.get(`/cms/evidence/${assessmentType}/${questionId}`),
  deleteEvidence: (assessmentType, questionId, evidenceId) => api.delete(`/cms/evidence/${assessmentType}/${questionId}/${evidenceId}`),
  downloadEvidence: (assessmentType, questionId, evidenceId) => api.get(`/cms/evidence/${assessmentType}/${questionId}/${evidenceId}/download`, { responseType: 'blob' }),
  getEvidenceSummary: (assessmentType) => api.get(`/cms/evidence-summary/${assessmentType}`),
  // Public evidence endpoints (for assessors)
  getPublicEvidence: (assessmentType, questionId) => api.get(`/cms/evidence-public/${assessmentType}/${questionId}`),
  downloadPublicEvidence: (assessmentType, questionId, evidenceId) => api.get(`/cms/evidence-public/${assessmentType}/${questionId}/${evidenceId}/download`, { responseType: 'blob' }),
};

// Share Link API
export const shareAPI = {
  createShareLink: (assessmentId, expiresInDays = 30) => api.post(`/assessment/${assessmentId}/share`, { expiresInDays }),
  getShareLinkStatus: (assessmentId) => api.get(`/assessment/${assessmentId}/share`),
  revokeShareLink: (assessmentId) => api.delete(`/assessment/${assessmentId}/share`),
  // Public endpoint (no auth needed)
  getSharedAssessment: (token) => axios.get(`${API_BASE}/shared/${token}`),
};

// Client API (for client role users)
export const clientAPI = {
  getAssessments: () => api.get('/client/assessments'),
  getAssessmentDetails: (assessmentId) => api.get(`/client/assessment/${assessmentId}`),
  getDataRoomFiles: () => api.get('/client/dataroom'),
  getDataRoomDownloadUrl: (fileId) => `${BACKEND_URL}/api/client/dataroom/download/${fileId}`,
};

// Attachments API
export const attachmentsAPI = {
  upload: (assessmentId, questionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/${assessmentId}/${questionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getForQuestion: (assessmentId, questionId) => api.get(`/attachments/${assessmentId}/${questionId}`),
  delete: (attachmentId) => api.delete(`/attachments/${attachmentId}`),
};

// Resources API
export const resourcesAPI = {
  upload: (file, name, description, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description || '');
    formData.append('category', category || 'General');
    return api.post('/resources', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAll: () => api.get('/resources'),
  download: (resourceId) => api.get(`/resources/${resourceId}/download`),
  delete: (resourceId) => api.delete(`/resources/${resourceId}`),
};

// Findings API
export const findingsAPI = {
  getFindings: (assessmentId) => api.get(`/assessment/${assessmentId}/findings`),
};

// Deficiency Remediation API
export const deficiencyAPI = {
  getDeficiencies: (assessmentId) => api.get(`/reports/deficiency/${assessmentId}`),
  updateRemediation: (assessmentId, questionId, data) => 
    api.put(`/deficiency/${assessmentId}/${questionId}`, data),
  uploadDocument: (assessmentId, questionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/deficiency/${assessmentId}/${questionId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDocument: (assessmentId, questionId, documentId) =>
    api.delete(`/deficiency/${assessmentId}/${questionId}/document/${documentId}`),
  // Priority management
  updatePriority: (assessmentId, questionId, data) =>
    api.put(`/assessment/${assessmentId}/deficiency/${questionId}/priority`, data),
  bulkUpdatePriority: (assessmentId, updates) =>
    api.put(`/assessment/${assessmentId}/deficiencies/bulk-priority`, { updates }),
};

// News API
export const newsAPI = {
  getCybersecurityNews: () => api.get('/news/cybersecurity'),
};

// SSP (System Security Plan) API
export const sspAPI = {
  create: (data) => api.post('/ssp/create', data),
  getAll: () => api.get('/ssps'),
  get: (sspId) => api.get(`/ssp/${sspId}`),
  update: (sspId, data) => api.put(`/ssp/${sspId}`, data),
  delete: (sspId) => api.delete(`/ssp/${sspId}`),
  getControls: (sspId) => api.get(`/ssp/${sspId}/controls`),
  updateControl: (sspId, controlId, data) => api.put(`/ssp/${sspId}/control/${controlId}`, data),
};

// Assessment Templates API
export const templatesAPI = {
  create: (data) => api.post('/templates', data),
  getAll: () => api.get('/templates'),
  apply: (templateId, data) => api.post(`/templates/${templateId}/apply`, data),
  delete: (templateId) => api.delete(`/templates/${templateId}`),
};

// NIST CSF SSP Report API
export const nistCsfSspAPI = {
  getSummary: (assessmentId) => api.get(`/ssp-report/nist-csf/${assessmentId}/summary`),
  getComplianceScore: (assessmentId) => api.get(`/ssp-report/nist-csf/${assessmentId}/compliance-score`),
  getDetailedControls: (assessmentId, gapsOnly = false) => api.get(`/ssp-report/nist-csf/${assessmentId}/detailed-controls?show_gaps_only=${gapsOnly}`),
  getEvidenceChecklist: (assessmentId) => api.get(`/ssp-report/nist-csf/${assessmentId}/evidence-checklist`),
  getPrioritization: (assessmentId) => api.get(`/ssp-report/nist-csf/${assessmentId}/prioritization`),
};

// Bookmarks API
export const bookmarksAPI = {
  toggle: (assessmentId, questionId) => api.post(`/assessment/${assessmentId}/bookmark/${questionId}`),
  getAll: (assessmentId) => api.get(`/assessment/${assessmentId}/bookmarks`),
};

// Bulk Actions API
export const bulkActionsAPI = {
  bulkAnswer: (assessmentId, data) => api.post(`/assessment/${assessmentId}/bulk-answer`, data),
  bulkEffortPriority: (assessmentId, data) => api.post(`/assessment/${assessmentId}/bulk-effort-priority`, data),
};

// Assessment Changelog API
export const changelogAPI = {
  get: (assessmentId, options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    if (options.userFilter) params.append('user_filter', options.userFilter);
    if (options.changeType) params.append('change_type', options.changeType);
    return api.get(`/assessment/${assessmentId}/changelog?${params.toString()}`);
  },
  clear: (assessmentId) => api.delete(`/assessment/${assessmentId}/changelog`),
  exportCSV: (assessmentId, options = {}) => {
    const params = new URLSearchParams();
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    if (options.userFilter) params.append('user_filter', options.userFilter);
    if (options.changeType) params.append('change_type', options.changeType);
    return `${API_URL}/assessment/${assessmentId}/changelog/export/csv?${params.toString()}`;
  },
  exportPDF: (assessmentId, options = {}) => {
    const params = new URLSearchParams();
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    if (options.userFilter) params.append('user_filter', options.userFilter);
    if (options.changeType) params.append('change_type', options.changeType);
    return `${API_URL}/assessment/${assessmentId}/changelog/export/pdf?${params.toString()}`;
  },
};

// Audit Trail API
export const auditAPI = {
  getForAssessment: (assessmentId) => api.get(`/audit/assessment/${assessmentId}`),
  getRecent: () => api.get('/audit/recent'),
};

// Section Comments API
export const commentsAPI = {
  add: (assessmentId, data) => api.post(`/assessment/${assessmentId}/comment`, data),
  getAll: (assessmentId) => api.get(`/assessment/${assessmentId}/comments`),
  delete: (assessmentId, commentId) => api.delete(`/assessment/${assessmentId}/comment/${commentId}`),
};

// Vulnerability Management API
export const vulnerabilitiesAPI = {
  create: (data) => api.post('/vulnerabilities', data),
  getAll: () => api.get('/vulnerabilities'),
  getStats: () => api.get('/vulnerabilities/stats'),
  update: (vulnId, data) => api.put(`/vulnerabilities/${vulnId}`, data),
  addNote: (vulnId, data) => api.post(`/vulnerabilities/${vulnId}/note`, data),
  delete: (vulnId) => api.delete(`/vulnerabilities/${vulnId}`),
  bulkImport: (data) => api.post('/vulnerabilities/import', data),
};

// Export API
export const exportAPI = {
  getExcelData: (assessmentId) => api.get(`/assessment/${assessmentId}/export/excel`),
};

// Compliance Calendar API
export const calendarAPI = {
  create: (data) => api.post('/calendar/events', data),
  getAll: () => api.get('/calendar/events'),
  update: (eventId, data) => api.put(`/calendar/events/${eventId}`, data),
  delete: (eventId) => api.delete(`/calendar/events/${eventId}`),
  addMilestone: (eventId, data) => api.post(`/calendar/events/${eventId}/milestone`, data),
  toggleMilestone: (eventId, milestoneId) => api.put(`/calendar/events/${eventId}/milestone/${milestoneId}`),
};

// Security Scorecard API
export const scorecardAPI = {
  get: () => api.get('/scorecard'),
};

// Incident Response API
export const incidentsAPI = {
  create: (data) => api.post('/incidents', data),
  getAll: () => api.get('/incidents'),
  get: (incidentId) => api.get(`/incidents/${incidentId}`),
  update: (incidentId, data) => api.put(`/incidents/${incidentId}`, data),
  addTimeline: (incidentId, data) => api.post(`/incidents/${incidentId}/timeline`, data),
  delete: (incidentId) => api.delete(`/incidents/${incidentId}`),
};

// Risk Register API
export const risksAPI = {
  create: (data) => api.post('/risks', data),
  getAll: () => api.get('/risks'),
  update: (riskId, data) => api.put(`/risks/${riskId}`, data),
  delete: (riskId) => api.delete(`/risks/${riskId}`),
  getStats: () => api.get('/risks/stats'),
};

// Policy Library API
export const policiesAPI = {
  create: (data) => api.post('/policies', data),
  getAll: () => api.get('/policies'),
  get: (policyId) => api.get(`/policies/${policyId}`),
  update: (policyId, data) => api.put(`/policies/${policyId}`, data),
  acknowledge: (policyId) => api.post(`/policies/${policyId}/acknowledge`),
  delete: (policyId) => api.delete(`/policies/${policyId}`),
};

// Asset Inventory API
export const assetsAPI = {
  create: (data) => api.post('/assets', data),
  getAll: () => api.get('/assets'),
  get: (assetId) => api.get(`/assets/${assetId}`),
  update: (assetId, data) => api.put(`/assets/${assetId}`, data),
  delete: (assetId) => api.delete(`/assets/${assetId}`),
  getStats: () => api.get('/assets/stats'),
};

// Penetration Testing API
export const pentestAPI = {
  createScan: (data) => api.post('/pentest/scan', data),
  getScans: () => api.get('/pentest/scans'),
  getScan: (scanId) => api.get(`/pentest/scan/${scanId}`),
  startScan: (scanId) => api.post(`/pentest/scan/${scanId}/start`),
  deleteScan: (scanId) => api.delete(`/pentest/scan/${scanId}`),
  getStats: () => api.get('/pentest/stats'),
  quickScan: (data) => api.post('/pentest/quick-scan', data),
  // Shodan integration
  shodanRecon: (data) => api.post('/pentest/shodan-recon', data),
  shodanStatus: () => api.get('/pentest/shodan-status'),
};

// Organizations API
export const organizationsAPI = {
  create: (data) => api.post('/organizations', data),
  getAll: () => api.get('/organizations'),
  getCurrent: () => api.get('/organizations/current'),
  switchOrg: (orgId) => api.put(`/organizations/current/${orgId}`),
  get: (orgId) => api.get(`/organizations/${orgId}`),
  update: (orgId, data) => api.put(`/organizations/${orgId}`, data),
  delete: (orgId) => api.delete(`/organizations/${orgId}`),
  // Members
  getMembers: (orgId) => api.get(`/organizations/${orgId}/members`),
  inviteMember: (orgId, data) => api.post(`/organizations/${orgId}/invite`, data),
  addMember: (orgId, data) => api.post(`/organizations/${orgId}/members`, data),
  updateMemberRole: (orgId, userId, data) => api.put(`/organizations/${orgId}/members/${userId}`, data),
  removeMember: (orgId, userId) => api.delete(`/organizations/${orgId}/members/${userId}`),
  // Invites
  getInvites: (orgId) => api.get(`/organizations/${orgId}/invites`),
  cancelInvite: (orgId, inviteId) => api.delete(`/organizations/${orgId}/invites/${inviteId}`),
  getInviteDetails: (token) => api.get(`/organizations/invite/${token}`),
  acceptInvite: (token) => api.post(`/organizations/invite/accept/${token}`),
};

// Training API
export const trainingAPI = {
  // Campaigns
  getCampaigns: () => api.get('/training/campaigns'),
  createCampaign: (data) => api.post('/training/campaigns', data),
  getCampaign: (id) => api.get(`/training/campaigns/${id}`),
  updateCampaign: (id, data) => api.put(`/training/campaigns/${id}`, data),
  updateCampaignStatus: (id, data) => api.put(`/training/campaigns/${id}/status`, data),
  deleteCampaign: (id) => api.delete(`/training/campaigns/${id}`),
  assignEmployees: (id, data) => api.post(`/training/campaigns/${id}/assign`, data),
  // Employees
  getEmployees: () => api.get('/training/employees'),
  createEmployee: (data) => api.post('/training/employees', data),
  bulkCreateEmployees: (data) => api.post('/training/employees/bulk', data),
  getEmployee: (id) => api.get(`/training/employees/${id}`),
  deleteEmployee: (id) => api.delete(`/training/employees/${id}`),
  // Assignments
  updateAssignmentProgress: (id, data) => api.put(`/training/assignments/${id}/progress`, data),
  // Phishing
  getPhishingSimulations: () => api.get('/training/phishing'),
  createPhishingSimulation: (data) => api.post('/training/phishing', data),
  sendPhishingSimulation: (id, data) => api.post(`/training/phishing/${id}/send`, data),
  recordPhishingAction: (id, data) => api.post(`/training/phishing/results/${id}/action`, data),
  // Stats
  getStats: () => api.get('/training/stats'),
  getDashboard: () => api.get('/training/dashboard'),
  // Training Modules (Content Library)
  getModules: (category) => api.get(`/training/modules${category ? `?category=${category}` : ''}`),
  getModuleCategories: () => api.get('/training/modules/categories'),
  getModule: (id) => api.get(`/training/modules/${id}`),
  startModule: (id) => api.post(`/training/modules/${id}/start`),
  completeModule: (id, data) => api.post(`/training/modules/${id}/complete`, data),
  getMyProgress: () => api.get('/training/my-progress'),
};

// Vendor Risk Management API
export const vendorsAPI = {
  getAll: () => api.get('/vendors'),
  create: (data) => api.post('/vendors', data),
  get: (id) => api.get(`/vendors/${id}`),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  updateStatus: (id, data) => api.put(`/vendors/${id}/status`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  addCertification: (id, data) => api.post(`/vendors/${id}/certifications`, data),
  getQuestionnaire: () => api.get('/vendors/questionnaire'),
  getStats: () => api.get('/vendors/stats'),
  // Assessments
  createAssessment: (vendorId, data) => api.post(`/vendors/${vendorId}/assessments`, data),
  getAssessment: (vendorId, assessmentId) => api.get(`/vendors/${vendorId}/assessments/${assessmentId}`),
  updateAssessment: (vendorId, assessmentId, data) => api.put(`/vendors/${vendorId}/assessments/${assessmentId}`, data),
  // SIG Lite Questionnaire
  getSigLiteQuestionnaire: () => api.get('/vendors/questionnaire/sig-lite'),
  createSigLiteAssessment: (vendorId, data) => api.post(`/vendors/${vendorId}/assessments/sig-lite`, data),
  updateSigLiteAssessment: (vendorId, assessmentId, data) => api.put(`/vendors/${vendorId}/assessments/${assessmentId}/sig-lite`, data),
};

// Dark Web Monitoring API
export const darkwebAPI = {
  // Monitored emails
  getMonitored: () => api.get('/darkweb/monitored'),
  addMonitored: (data) => api.post('/darkweb/monitored', data),
  bulkAddMonitored: (data) => api.post('/darkweb/monitored/bulk', data),
  removeMonitored: (id) => api.delete(`/darkweb/monitored/${id}`),
  checkEmail: (id) => api.post(`/darkweb/monitored/${id}/check`),
  // Breaches
  getBreaches: (params) => api.get('/darkweb/breaches', { params }),
  getBreach: (id) => api.get(`/darkweb/breaches/${id}`),
  updateBreachStatus: (id, data) => api.put(`/darkweb/breaches/${id}/status`, data),
  // Password check
  checkPassword: (data) => api.post('/darkweb/password-check', data),
  // Alerts
  getAlerts: (unreadOnly) => api.get(`/darkweb/alerts${unreadOnly ? '?unread_only=true' : ''}`),
  markAlertRead: (id) => api.put(`/darkweb/alerts/${id}/read`),
  markAllAlertsRead: () => api.put('/darkweb/alerts/read-all'),
  // Stats
  getStats: () => api.get('/darkweb/stats'),
};

// Control Mapping API
export const controlsAPI = {
  getFrameworks: () => api.get('/controls/frameworks'),
  getMappings: () => api.get('/controls/mappings'),
  getFamilyMappings: (family) => api.get(`/controls/mappings/${family}`),
  gapAnalysis: (data) => api.post('/controls/gap-analysis', data),
  getStatuses: (framework) => api.get(`/controls/status${framework ? `?framework=${framework}` : ''}`),
  updateStatus: (data) => api.post('/controls/status', data),
  bulkUpdateStatuses: (data) => api.post('/controls/status/bulk', data),
  getDashboard: () => api.get('/controls/dashboard'),
};

// System Reports & Alerts API
export const systemReportsAPI = {
  // Scheduled reports
  getScheduled: () => api.get('/reports/scheduled'),
  createScheduled: (data) => api.post('/reports/scheduled', data),
  deleteScheduled: (id) => api.delete(`/reports/scheduled/${id}`),
  // Generated reports
  generate: (data) => api.post('/reports/generate', data),
  getGenerated: () => api.get('/reports/generated'),
  getGeneratedReport: (id) => api.get(`/reports/generated/${id}`),
  // System alerts
  getAlerts: (unreadOnly) => api.get(`/reports/alerts${unreadOnly ? '?unread_only=true' : ''}`),
  getAlertCount: () => api.get('/reports/alerts/count'),
  markAlertRead: (id) => api.put(`/reports/alerts/${id}/read`),
  dismissAlert: (id) => api.put(`/reports/alerts/${id}/dismiss`),
  markAllAlertsRead: () => api.put('/reports/alerts/read-all'),
  checkAlerts: () => api.post('/reports/check-alerts'),
};

// Evidence Collection API
export const evidenceAPI = {
  // Categories
  getCategories: () => api.get('/evidence/categories'),
  // Upload
  upload: (formData) => api.post('/evidence/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // List and Library
  list: (params) => api.get('/evidence/list', { params }),
  getLibrary: (params) => api.get('/evidence/library', { params }),
  // CRUD
  get: (id) => api.get(`/evidence/${id}`),
  update: (id, data) => api.put(`/evidence/${id}`, data),
  delete: (id) => api.delete(`/evidence/${id}`),
  deletePermanent: (id) => api.delete(`/evidence/${id}/permanent`),
  // Linking
  link: (data) => api.post('/evidence/link', data),
  unlink: (evidenceId, questionId) => api.delete(`/evidence/link/${evidenceId}/${questionId}`),
  // Question evidence
  getForQuestion: (assessmentId, questionId) => api.get(`/evidence/question/${assessmentId}/${questionId}`),
  // Stats
  getStats: (assessmentId) => api.get(`/evidence/stats/${assessmentId}`),
  // Download
  getDownloadUrl: (id) => `${API_BASE}/evidence/download/${id}`,
};

// Tenants API (Super Admin only)
export const tenantsAPI = {
  // CRUD
  list: () => api.get('/tenants'),
  create: (data) => api.post('/tenants', data),
  get: (id) => api.get(`/tenants/${id}`),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
  // Users and Stats
  getUsers: (id) => api.get(`/tenants/${id}/users`),
  getStats: (id) => api.get(`/tenants/${id}/stats`),
  // Analytics
  getAnalytics: (id) => api.get(`/tenants/${id}/analytics`),
  getAllAnalytics: () => api.get('/tenants/analytics/overview'),
};

// Enhanced Data Room API
export const dataRoomAPI = {
  // Folders
  createFolder: (data) => api.post('/dataroom/folders', data),
  getFolders: (clientId, parentId) => api.get(`/dataroom/folders/${clientId}`, { params: { parent_id: parentId } }),
  getFolderTree: (clientId) => api.get(`/dataroom/folders/${clientId}/tree`),
  updateFolder: (folderId, data) => api.put(`/dataroom/folders/${folderId}`, data),
  deleteFolder: (folderId) => api.delete(`/dataroom/folders/${folderId}`),
  
  // Files
  uploadFile: (formData) => api.post('/dataroom/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  bulkUpload: (formData) => api.post('/dataroom/upload/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getFiles: (clientId, params) => api.get(`/dataroom/files/${clientId}`, { params }),
  updateFile: (fileId, data) => api.put(`/dataroom/files/${fileId}`, data),
  deleteFile: (fileId, permanent = false) => api.delete(`/dataroom/files/${fileId}`, { params: { permanent } }),
  downloadFile: (fileId) => `${API_BASE}/dataroom/download/${fileId}`,
  
  // Versions
  getVersions: (fileId) => api.get(`/dataroom/files/${fileId}/versions`),
  restoreVersion: (fileId, version) => api.post(`/dataroom/files/${fileId}/restore/${version}`),
  
  // Access Logs
  getAccessLog: (fileId) => api.get(`/dataroom/files/${fileId}/access-log`),
  
  // Sharing
  createShareLink: (fileId, data) => api.post(`/dataroom/share/${fileId}`, data),
  getShareLinks: (fileId) => api.get(`/dataroom/share/list/${fileId}`),
  revokeShareLink: (shareId) => api.delete(`/dataroom/share/${shareId}`),
  
  // Expiration Alerts
  getExpiringFiles: (clientId, days = 30) => api.get(`/dataroom/expiring/${clientId}`, { params: { days } }),
  
  // Templates & Categories
  getTemplates: () => api.get('/dataroom/templates'),
  getCategories: () => api.get('/dataroom/categories'),
  getTags: (clientId) => api.get(`/dataroom/tags/${clientId}`),
  
  // Stats
  getStats: (clientId) => api.get(`/dataroom/stats/${clientId}`),
};


export default api;
